import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, Package, Building2, Clock, Camera,
  AlertTriangle, Truck, Image as ImageIcon
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PO_STATUSES = {
  draft: { label: "Draft", color: "bg-gray-500/20 text-gray-400" },
  sent: { label: "Sent", color: "bg-blue-500/20 text-blue-400" },
  confirmed: { label: "Confirmed", color: "bg-purple-500/20 text-purple-400" },
  partially_received: { label: "Partial", color: "bg-orange-500/20 text-orange-400" },
  received: { label: "Received", color: "bg-green-500/20 text-green-400" },
  completed: { label: "Completed", color: "bg-green-600/20 text-green-500" },
  cancelled: { label: "Cancelled", color: "bg-red-500/20 text-red-400" }
};

const PublicPOViewPage = () => {
  const { shareToken } = useParams();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    loadPO();
  }, [shareToken]);

  const loadPO = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/enterprise/po/public/${shareToken}`);
      setPo(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Purchase order not found or link expired');
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshot = async () => {
    if (!contentRef.current) return;
    
    setCapturing(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#1a1a2e',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${po.po_number}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Screenshot saved!');
      }, 'image/png');
    } catch (err) {
      toast.error('Failed to capture screenshot');
      console.error(err);
    } finally {
      setCapturing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/50 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Link Invalid</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const statusConfig = PO_STATUSES[po.status] || PO_STATUSES.draft;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 md:p-8">
      {/* Screenshot Button - Fixed position */}
      <div className="fixed top-4 right-4 z-50">
        <Button
          onClick={handleScreenshot}
          disabled={capturing}
          className="bg-amber-500 hover:bg-amber-600 text-black font-semibold shadow-lg"
        >
          <Camera className="h-4 w-4 mr-2" />
          {capturing ? 'Capturing...' : 'Screenshot'}
        </Button>
      </div>

      <div className="max-w-4xl mx-auto" ref={contentRef}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-4">
            <FileText className="h-5 w-5 text-amber-400" />
            <span className="text-amber-400 font-medium">Purchase Order</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{po.po_number}</h1>
          <Badge className={`${statusConfig.color} border-0 text-sm`}>
            {statusConfig.label}
          </Badge>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl overflow-hidden"
        >
          {/* PO Info - Simplified */}
          <div className="p-6 border-b border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Building2 className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Vendor</p>
                  <p className="text-white font-medium">{po.vendor_name}</p>
                </div>
              </div>
              
              {po.linked_order_number && (
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-teal-500/10">
                    <Truck className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Linked Order{po.linked_orders?.length > 1 ? 's' : ''}</p>
                    <div className="flex flex-wrap gap-1">
                      {po.linked_orders && po.linked_orders.length > 0 ? (
                        po.linked_orders.map((orderNum, idx) => (
                          <span key={idx} className="text-white font-medium bg-teal-500/20 px-2 py-0.5 rounded text-sm">
                            {orderNum}
                          </span>
                        ))
                      ) : (
                        <span className="text-white font-medium">{po.linked_order_number}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-gray-500/10">
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Created</p>
                  <p className="text-white font-medium">
                    {po.created_at ? new Date(po.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items with Images */}
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-400" />
              Items ({po.line_items?.length || 0})
            </h2>
            
            <div className="space-y-4">
              {po.line_items?.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.05 }}
                  className="bg-gray-700/30 rounded-xl p-4"
                >
                  <div className="flex items-start gap-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.sku}
                          className="w-20 h-20 object-cover rounded-lg border border-gray-600"
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-700 rounded-lg border border-gray-600 flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-500" />
                        </div>
                      )}
                    </div>
                    
                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-amber-400 font-mono font-bold text-lg">{item.sku}</span>
                        {item.variant_title && (
                          <span className="text-gray-300">- {item.variant_title}</span>
                        )}
                      </div>
                      {item.vendor_sku && (
                        <p className="text-sm text-gray-500">Vendor SKU: {item.vendor_sku}</p>
                      )}
                      {item.notes && (
                        <p className="text-sm text-gray-400 mt-2 bg-gray-700/50 rounded px-2 py-1">
                          {item.notes}
                        </p>
                      )}
                    </div>
                    
                    {/* Quantity */}
                    <div className="flex-shrink-0 text-right">
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2">
                        <span className="text-amber-400 font-bold text-2xl">{item.quantity}</span>
                        <span className="text-amber-400/70 text-sm ml-1">units</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Vendor Notes */}
          {po.vendor_notes && (
            <div className="p-6 border-t border-gray-700 bg-gray-700/20">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Notes for Vendor</h3>
              <p className="text-white">{po.vendor_notes}</p>
            </div>
          )}
        </motion.div>

        {/* Footer - Simplified */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <p className="text-gray-600 text-xs">
            Powered by{' '}
            <a
              href="https://opswarex.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-amber-500 underline-offset-2 hover:underline transition-colors"
              data-testid="powered-by-opswarex-link"
            >
              Opswarex
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PublicPOViewPage;
